import type { LucideIcon } from "lucide-react";
import {
  Database,
  Sparkles,
  ShieldCheck,
  Eye,
  BarChart3,
} from "lucide-react";

// ── Phase Config ──

export type Phase =
  | "extraction"
  | "generation"
  | "evaluator"
  | "review"
  | "coverage";

export interface PhaseConfig {
  id: Phase;
  label: string;
  icon: LucideIcon;
}

export const PHASES: PhaseConfig[] = [
  { id: "extraction", label: "Extraction", icon: Database },
  { id: "generation", label: "Generation", icon: Sparkles },
  { id: "evaluator", label: "Evaluators", icon: ShieldCheck },
  { id: "review", label: "Review", icon: Eye },
  { id: "coverage", label: "Scale", icon: BarChart3 },
];

// ── Real DB Types ──

export interface Skill {
  id: string;
  standard_code: string;
  /** String, not number — the backend's SkillResponse.grade is `str`, and a
   *  K-12 bank contains "K". */
  grade: string;
  skill_name: string;
  source: string;
  problem_count: number;
  domain?: string;
}

export interface Problem {
  id: string;
  skill_id: string;
  standard_code: string;
  grade: number;
  skill_name: string;
  difficulty_level: string;
  question_type: string;
  question_text: string;
  answer_choices: string[] | null;
  correct_answer: string;
  images: string[] | null;
  explanation_steps: string[] | null;
  hint_text: string[] | null;
  surveyjs_config: Record<string, unknown> | null;
  source: string;
  source_id: string;
  source_url: string;
  reviewed: boolean;
  active: boolean;
  soft_deleted: boolean;
  is_evertutor: boolean;
  created_at: string;
  updated_at: string;
}

// ── Mock data for phases that don't have APIs yet ──

export interface EvalResult {
  math: boolean;
  standard: boolean;
  difficulty: boolean;
  originality: number;
  verdict: "pass" | "fail" | "review";
}

export interface GeneratedProblem {
  id: string;
  skill: string;
  text: string;
  type: "mcq" | "fill_in" | "input";
  difficulty: "mild" | "medium" | "spicy";
  options?: string[];
  answer: string;
  eval: EvalResult;
}

export interface Student {
  id: string;
  name: string;
  current: string;
  level: string;
  mastery: number;
}

export const GENERATED_DATA: GeneratedProblem[] = [
  { id: "GEN-001", skill: "4.NF.A.1", text: "Which fraction is the same as 4/8?", type: "mcq", difficulty: "mild", options: ["1/2", "3/8", "2/6", "4/6"], answer: "1/2", eval: { math: true, standard: true, difficulty: true, originality: 0.78, verdict: "pass" } },
  { id: "GEN-002", skill: "4.NF.A.1", text: "Find an equivalent fraction for 6/9.", type: "mcq", difficulty: "mild", options: ["2/3", "3/4", "1/3", "6/8"], answer: "2/3", eval: { math: true, standard: true, difficulty: true, originality: 0.82, verdict: "pass" } },
  { id: "GEN-003", skill: "4.NF.B.3a", text: "Calculate 4/7 + 2/7.", type: "fill_in", difficulty: "mild", answer: "6/7", eval: { math: true, standard: true, difficulty: false, originality: 0.91, verdict: "review" } },
  { id: "GEN-004", skill: "4.OA.A.1", text: "Jake collected 4x as many shells as Mia. Mia: 12. Jake?", type: "input", difficulty: "medium", answer: "46", eval: { math: false, standard: true, difficulty: true, originality: 0.85, verdict: "fail" } },
];

export const STUDENTS_DATA: Student[] = [
  { id: "S1", name: "Emma", current: "4.NF.A.2", level: "medium", mastery: 72 },
  { id: "S2", name: "Liam", current: "4.NF.A.1", level: "spicy", mastery: 91 },
  { id: "S3", name: "Sofia", current: "3.OA.A.1", level: "mild", mastery: 34 },
];

export const COVERAGE_SKILLS_DATA = [
  { id: "SK-001", grade: 4, standards: ["4.NF.A.1", "4.NF.A.2"], name: "Equivalent Fractions", problems: [{ difficulty: "mild" }, { difficulty: "mild" }, { difficulty: "medium" }, { difficulty: "medium" }, { difficulty: "spicy" }], problemCount: 5, status: "complete" },
  { id: "SK-002", grade: 4, standards: ["4.NF.A.2"], name: "Compare Fractions (Like Denominators)", problems: [{ difficulty: "mild" }, { difficulty: "medium" }, { difficulty: "medium" }, { difficulty: "spicy" }], problemCount: 4, status: "complete" },
  { id: "SK-003", grade: 4, standards: ["4.NF.B.3a", "4.NF.B.3b"], name: "Add Fractions (Like Denominators)", problems: [{ difficulty: "mild" }, { difficulty: "mild" }, { difficulty: "medium" }, { difficulty: "medium" }, { difficulty: "spicy" }], problemCount: 5, status: "complete" },
  { id: "SK-004", grade: 4, standards: ["4.OA.A.1", "4.OA.A.2"], name: "Multiplicative Comparisons", problems: [{ difficulty: "medium" }, { difficulty: "medium" }, { difficulty: "mild" }], problemCount: 3, status: "partial" },
  { id: "SK-005", grade: 4, standards: ["4.OA.A.3"], name: "Multi-step Word Problems", problems: [], problemCount: 0, status: "gap" },
  { id: "SK-006", grade: 4, standards: ["4.MD.A.1", "4.MD.A.2"], name: "Measurement Conversions", problems: [{ difficulty: "mild" }, { difficulty: "medium" }, { difficulty: "spicy" }], problemCount: 3, status: "partial" },
  { id: "SK-007", grade: 3, standards: ["3.OA.A.1"], name: "Interpret Products", problems: [{ difficulty: "mild" }, { difficulty: "mild" }, { difficulty: "medium" }], problemCount: 3, status: "partial" },
  { id: "SK-008", grade: 3, standards: ["3.NF.A.1"], name: "Understand Fractions", problems: [{ difficulty: "mild" }, { difficulty: "mild" }, { difficulty: "medium" }, { difficulty: "medium" }, { difficulty: "spicy" }], problemCount: 5, status: "complete" },
  { id: "SK-009", grade: 5, standards: ["5.NF.A.1"], name: "Add Fractions (Unlike Denominators)", problems: [{ difficulty: "mild" }, { difficulty: "medium" }], problemCount: 2, status: "partial" },
  { id: "SK-010", grade: 5, standards: ["5.NF.B.3"], name: "Fractions as Division", problems: [], problemCount: 0, status: "gap" },
];

export const ADAPTIVE_PROBLEMS = [
  { q: "Which fraction equals 2/4?", opts: ["1/2", "3/4", "2/3", "1/4"], correct: "1/2", diff: "mild" as const },
  { q: "Compare: 3/5 ___ 3/8", opts: [">", "<", "="], correct: ">", diff: "medium" as const },
  { q: "6/10 = ?/5", opts: ["3", "4", "6", "2"], correct: "3", diff: "medium" as const },
];

import { resolveImageUrl as resolveOne, S3_MEDIA_BASE } from "./media";
import {
  normalizeImgTag,
  normalizeImgTagJson,
  IMG_TAG_RE,
  JSON_IMG_TAG_RE,
} from "./problem-text";

/**
 * Resolve image srcs inside a JSON.stringify'd config. Output stays valid JSON.
 * Unresolvable <img> tags are removed, so this is not purely in-place.
 */
export function resolveImageSrcsInJson(
  json: string,
  fallbacks: { url: string; alt: string }[] = [],
): string {
  // Whole-tag pass. Configs often contain src="[object Object]" (the scraper
  // interpolated an object), so repair from `fallbacks` where possible and drop
  // the tag otherwise — an empty src renders as raw text in SurveyJS.
  let fallbackIndex = 0;
  let out = json.replace(JSON_IMG_TAG_RE, (tag: string) => {
    // Read src from the matched tag rather than capturing it in the tag
    // pattern, so the pattern can stay quote-aware. A tag with no src never
    // consumed a fallback and still must not, or the accounting shifts.
    const srcAttr = /\ssrc\s*=\s*\\"((?:[^"\\]|\\.)*)\\"/i.exec(tag);
    if (!srcAttr) return normalizeImgTagJson(tag);

    const url = srcAttr[1] ?? "";
    let resolved = url ? resolveOne(url) : null;
    let alt = "";
    if (!resolved) {
      const next = fallbacks[fallbackIndex];
      if (next) {
        fallbackIndex += 1;
        resolved = next.url;
        alt = next.alt;
      }
    }
    if (!resolved) return "";

    // Must stay anchored on the escaped quote: Firebase URLs contain
    // "?alt=media", which a looser /alt=/ test matches.
    const hasAltAttr = /\salt=\\"/.test(tag);

    let repaired = tag.replace(`src=\\"${url}\\"`, `src=\\"${resolved}\\"`);
    if (alt && !hasAltAttr) {
      // The alt comes from the `images` column and is untrusted. Quotes must
      // not survive into the attribute, or it terminates early and the rest of
      // the tag — src included — is reinterpreted as attributes.
      const escaped = alt.replace(/\\/g, "").replace(/"/g, "'");
      repaired = repaired.replace(/^<img/, `<img alt=\\"${escaped}\\"`);
    }
    // Re-emits src="…" verbatim, so preprocessConfig's placement probe still
    // matches — pinned by a test in question-bank-qa-fixes.
    return normalizeImgTagJson(repaired);
  });

  // Any other src="..." attribute (not on an <img>).
  out = out.replace(/src=\\"([^"\\]+)\\"/g, (m: string, url: string) => {
    const resolved = resolveOne(url);
    return resolved ? `src=\\"${resolved}\\"` : m;
  });
  // Any remaining bare graphie ref (e.g. inside markdown in a title or choice).
  // This previously stripped the scheme to plain https:// with no extension,
  // which Khan's CDN serves as 403 — it manufactured broken images. Map to the
  // S3 mirror instead, consistent with the mirror-only policy in ./media.
  out = out.replace(
    /web\+graphie:\/\/cdn\.kastatic\.org\/ka-perseus-graphie\/([A-Za-z0-9_-]+)/g,
    (_m, hash: string) => `${S3_MEDIA_BASE}/ka/${hash.toLowerCase()}.svg`,
  );

  return out;
}

/**
 * For rendered HTML/text: resolve markdown images and img src attrs, replacing
 * unresolvable refs with a chip. NOT safe for raw JSON — use the Json variant.
 */
export function resolveImageSrcsInText(text: string): string {
  let out = text;

  // Markdown images: ![alt](url) → <img>
  out = out.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt: string, url: string) => {
    const resolved = resolveOne(url);
    // Unresolvable refs used to collapse to "" — the figure just vanished from
    // the question with no trace. Emit the chip markup instead.
    if (!resolved) {
      const safe = alt || url;
      return `<span class="qb-chip qb-unresolved" title="${url.replace(/"/g, "&quot;")}"><span>figure not mirrored — ${safe.replace(/</g, "&lt;")}</span></span>`;
    }
    // Alt text is scraped and routinely contains quotes ('the "unit" square').
    // Unescaped, it closes the attribute early and the rest of the tag is
    // reparsed as attributes, which loses the image.
    const safeAlt = alt.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
    return `<img src="${resolved}" alt="${safeAlt}" loading="lazy" class="qb-inline-img" />`;
  });

  // HTML img src="ka/..." or src="web+graphie://..."
  out = out.replace(/src="([^"]+)"/g, (_m, url: string) => {
    const resolved = resolveOne(url);
    return resolved ? `src="${resolved}"` : `src=""`;
  });

  // Remove broken img tags (empty src or [object Object]), then normalize what
  // survives. Both passes are quote-aware: `alt="a > b"` is ordinary figure alt
  // text, and matching to the first `>` truncated the tag mid-attribute.
  out = out.replace(IMG_TAG_RE, (tag) => {
    if (/\ssrc\s*=\s*"(?:|\[object Object\])"/i.test(tag)) return "";
    // Runs over the markdown-built tags too. That is not a no-op — they are
    // re-emitted in normalized attribute order — but it is idempotent, and it
    // is the only pass that reaches a literal tag, which SurveyJS injects past
    // DOMPurify with its scraped style intact.
    return normalizeImgTag(tag);
  });

  return out;
}

// ── Helpers ──

// Text rendering lives in ./problem-text — re-exported so existing importers
// (SurveyRenderer) are unaffected.
export { cleanLatex, problemTextToHtml, problemTextToPreview } from "./problem-text";

/**
 * Coerce an API boolean that may have arrived as a string.
 *
 * The studio backend's `_serialize_row` stringifies any non-list/dict value, so
 * Postgres booleans reach us as "True"/"False" — and "False" is truthy in JS,
 * which made the "reviewed" chip show on every problem.
 */
export function toBool(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "t" || s === "1" || s === "yes";
  }
  if (typeof v === "number") return v !== 0;
  return false;
}

export function statusColor(s: string): string {
  if (s === "complete" || s === "pass") return "bg-emerald-500";
  if (s === "partial" || s === "review") return "bg-amber-400";
  if (s === "gap" || s === "fail") return "bg-red-400";
  return "bg-neutral-300";
}

export function difficultyColor(d: string): "default" | "secondary" | "destructive" | "outline" {
  if (d === "easy") return "secondary";
  if (d === "hard") return "destructive";
  return "outline";
}
